import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button, ButtonGroup, Heading, Modal, TextButton } from '@zillow/constellation-10';
import {
  useDeletePaymentMethodWebApiHandler_deletePaymentMethod,
  useReauthenticatePaymentMethodWebApiHandler_reauthenticateRenterPaymentMethod,
} from '@zg-rentals/ts-rent-payment-client-react-query';
import { logError } from '@zg-rentals/log-error';

import type {
  INewPaymentMethods,
  RenterConfigInterface,
  RenterMoveInCostsDetailsInterface,
  RenterPaymentMethodInterface,
  RenterPaymentMethodTypeInterface,
  RenterPaymentSubscriptionInterface,
} from '@renter-hub/models/Types';
import { useCreateRenterPaymentMethod } from '@renter-hub/hooks/useCreatePaymentMethod';
import { DisplayPaymentMethods } from './modules/DisplayPaymentMethods';
import { VerifyBankAccount } from './modules/VerifyBankAccount';
import { MustHaveAtLeastOnePaymentMethod } from './modules/MustHaveAtLeastOnePaymentMethod';
import PaymentMethodDetail from './modules/PaymentMethodDetail';
import { DeletePaymentMethodConfirmation } from './modules/DeletePaymentMethodConfirmation';
import { DeleteOneOtherActivePaymentMethod } from './modules/DeleteOneOtherActivePaymentMethod';
import { DeleteScheduledPaymentMethod } from './modules/DeleteScheduledPaymentMethod';
import DebouncedButton from '@common/components/ConstellationDebouncedButton';
import { VerificationInProgress } from './modules/VerificationInProgress';
import PlaidButton from '@common/components/PlaidButton';
import NotificationContainer from '@common/components/NotificationContainer';
import userPersonas from '@common/constants/userPersonas';
import { getVerifiedPaymentMethods } from '@common/paymentsUtils/multiplePaymentMethodsUtil';
import { ReconnectBankAccount } from './modules/ReconnectBankAccount';
import { VERIFICATION_STATUS } from '@common/constants/PlaidRequirement';
import { DEFAULT_ERROR } from '@renter-hub/constants/notifications';
import { showModalNotification } from '@common/actions/NotificationActions';

interface Props {
  config: RenterConfigInterface;
  getPlaidUpdateModeLinkToken: (externalId?: string) => any;
  onPaymentMethodCreationCompletion: ({
    errorMessage,
    paymentMethod,
  }: {
    errorMessage?: string;
    paymentMethod?: any;
  }) => any;
  addressInfo: any;
  paymentMethods: Array<RenterPaymentMethodInterface>;
  onCancel: () => void;
  onDeletePaymentMethodClickSuccess: (encodedPaymentMethodId) => void;
  paymentSubscriptionDetails: RenterPaymentSubscriptionInterface;
  moveInCostsDetails: RenterMoveInCostsDetailsInterface;
  onSubmitDeletePaymentMethod: (items: any) => void;
  onAddNewPaymentClick: () => void;
  paymentMethodTypes: Array<RenterPaymentMethodTypeInterface>;
  trackValidationError: (...args: any) => any;
}

const STEPS = {
  DISPLAY_PAYMENT_METHODS: 1,
  DISPLAY_PAYMENT_METHOD_DETAILS: 2,
  DISPLAY_MUST_HAVE_AT_LEAST_ONE_PAYMENT_METHOD: 3,
  VERIFY_BANK_ACCOUNT: 4,
  DELETE_NOT_SCHEDULED_PAYMENT_METHOD: 6,
  DELETE_SCHEDULED_PAYMENT_METHOD_WHEN_ONE_OTHER_ACTIVE_PAYMENT_METHOD: 7,
  DELETE_SCHEDULED_PAYMENT_METHOD_WHEN_MULTIPLE_ACTIVE_PAYMENT_METHODS: 8,
  VERIFICATION_IN_PROGRESS: 9,
  RECONNECT_BANK_ACCOUNT: 10,
};
const ManagePaymentMethodsPage = ({
  config,
  getPlaidUpdateModeLinkToken,
  onPaymentMethodCreationCompletion,
  paymentMethods,
  addressInfo,
  paymentSubscriptionDetails,
  moveInCostsDetails,
  onCancel,
  onDeletePaymentMethodClickSuccess,
  onSubmitDeletePaymentMethod,
  onAddNewPaymentClick,
  paymentMethodTypes,
  trackValidationError,
}: Props) => {
  const dispatch = useDispatch();

  const { homeAddress, unit, city, state, zipCode } = addressInfo;
  const formattedAddress = `${homeAddress} ${unit ? `${unit} ` : ''}${city}, ${state} ${zipCode}`;
  const [paymentMethodToDelete, setPaymentMethodToDelete] = useState<RenterPaymentMethodInterface | null>();

  const [selectedMonthlyRentPaymentMethodDetails, setSelectedMonthlyRentPaymentMethodDetails] =
    useState<RenterPaymentMethodInterface | null>(null);

  const [selectedMoveInCostsPaymentMethodDetails, setSelectedMoveInCostsPaymentMethodDetails] =
    useState<RenterPaymentMethodInterface | null>(null);

  const [selectedNewPaymentMethods, setSelectedNewPaymentMethods] = useState<INewPaymentMethods>({
    moveInCostsId: '',
    monthlyRentId: '',
  });

  const [step, setStep] = useState<number>(STEPS.DISPLAY_PAYMENT_METHODS);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { mutateAsync: reauthenticatePaymentMethod } =
    useReauthenticatePaymentMethodWebApiHandler_reauthenticateRenterPaymentMethod({
      onSuccess: () => onPaymentMethodCreationCompletion({}),
      onError: (error) => {
        const message = error instanceof Error ? error.message : undefined;
        onPaymentMethodCreationCompletion({ errorMessage: message });
      },
    });

  const { mutateAsync: deletePaymentMethod } = useDeletePaymentMethodWebApiHandler_deletePaymentMethod({
    onSuccess: () => onDeletePaymentMethodClickSuccess(paymentMethodToDelete?.id),
    onError: (error) => {
      logError({ error });
      dispatch(showModalNotification(DEFAULT_ERROR));
    },
  });

  useEffect(() => {
    if (paymentMethodToDelete && step === STEPS.DISPLAY_PAYMENT_METHODS) {
      setStep(STEPS.DISPLAY_PAYMENT_METHOD_DETAILS);
    }
  }, [paymentMethodToDelete]);

  const { mutateAsync: createRenterPaymentMethod } = useCreateRenterPaymentMethod({
    onSuccess: (response) => onPaymentMethodCreationCompletion({ paymentMethod: response.paymentMethod }),
    onError: (error) => {
      const message = error instanceof Error ? error.message : undefined;
      onPaymentMethodCreationCompletion({ errorMessage: message });
    },
  });

  const onBackClick = () => {
    switch (step) {
      case STEPS.DISPLAY_PAYMENT_METHOD_DETAILS:
        setPaymentMethodToDelete(null);
        return setStep(STEPS.DISPLAY_PAYMENT_METHODS);
      case STEPS.VERIFY_BANK_ACCOUNT:
        return setStep(STEPS.DISPLAY_PAYMENT_METHODS);
      case STEPS.DISPLAY_MUST_HAVE_AT_LEAST_ONE_PAYMENT_METHOD:
        return setStep(STEPS.DISPLAY_PAYMENT_METHOD_DETAILS);
      case STEPS.DELETE_NOT_SCHEDULED_PAYMENT_METHOD:
        return setStep(STEPS.DISPLAY_PAYMENT_METHOD_DETAILS);
      case STEPS.DELETE_SCHEDULED_PAYMENT_METHOD_WHEN_ONE_OTHER_ACTIVE_PAYMENT_METHOD:
        return setStep(STEPS.DISPLAY_PAYMENT_METHOD_DETAILS);
      case STEPS.DELETE_SCHEDULED_PAYMENT_METHOD_WHEN_MULTIPLE_ACTIVE_PAYMENT_METHODS:
        return setStep(STEPS.DISPLAY_PAYMENT_METHOD_DETAILS);
      case STEPS.VERIFICATION_IN_PROGRESS:
        return setStep(STEPS.DISPLAY_PAYMENT_METHODS);
      case STEPS.RECONNECT_BANK_ACCOUNT:
        return setStep(STEPS.DISPLAY_PAYMENT_METHODS);
      default:
        return STEPS.DISPLAY_PAYMENT_METHODS;
    }
  };

  const onPlaidExit = (err, metadata) => {
    trackValidationError({
      plaidErrorsLastEncountered: err,
      plaidMetadata: metadata,
    });
  };

  const onContinueFromDeleteScheduledPaymentMethod = (payload) => {
    const { moveInCostsPaymentMethodId, monthlyRentPaymentMethodId } = payload;
    setSelectedMonthlyRentPaymentMethodDetails(
      paymentMethods.find((paymentMethod) => paymentMethod.id === monthlyRentPaymentMethodId) || null,
    );
    setSelectedMoveInCostsPaymentMethodDetails(
      paymentMethods.find((paymentMethod) => paymentMethod.id === moveInCostsPaymentMethodId) || null,
    );
    setStep(STEPS.DELETE_SCHEDULED_PAYMENT_METHOD_WHEN_ONE_OTHER_ACTIVE_PAYMENT_METHOD);
  };

  const renderPrimaryButton = () => {
    if (step === STEPS.DISPLAY_PAYMENT_METHODS) {
      return (
        <Button emphasis="filled" onClick={onAddNewPaymentClick}>
          Add new payment method
        </Button>
      );
    } else if (step === STEPS.DISPLAY_PAYMENT_METHOD_DETAILS && paymentMethodToDelete) {
      return (
        <Button emphasis="filled" onClick={onCancel}>
          Okay
        </Button>
      );
    } else if (step === STEPS.DISPLAY_MUST_HAVE_AT_LEAST_ONE_PAYMENT_METHOD && paymentMethodToDelete) {
      return (
        <Button emphasis="filled" onClick={onAddNewPaymentClick}>
          Add payment method
        </Button>
      );
    } else if ((step === STEPS.VERIFY_BANK_ACCOUNT || step === STEPS.RECONNECT_BANK_ACCOUNT) && paymentMethodToDelete) {
      return (
        <PlaidButton
          plaidEnv={config.plaidEnv}
          plaidLinkToken={config.plaidLinkToken}
          plaidLinkTokenExpiration={config.plaidLinkTokenExpiration}
          getPlaidLinkToken={() => getPlaidUpdateModeLinkToken(paymentMethodToDelete.id)}
          label={step === STEPS.VERIFY_BANK_ACCOUNT ? 'Verify with Plaid' : 'Reconnect with Plaid'}
          full={false}
          onSuccess={(data: any) => {
            if (step === STEPS.RECONNECT_BANK_ACCOUNT) {
              reauthenticatePaymentMethod({
                postData: {
                  encodedPaymentMethodId: paymentMethodToDelete.id,
                },
              });
            } else {
              createRenterPaymentMethod({
                postData: {
                  plaidToken: {
                    accountId: data.accountId,
                    publicToken: data.publicToken,
                  },
                  bankName: data.bankName,
                  mask: data.mask,
                  ...(data.status && { status: data.status }),
                },
              });
            }
          }}
          onExit={onPlaidExit}
          useConstellationButton={true}
        />
      );
    } else if (step === STEPS.DELETE_NOT_SCHEDULED_PAYMENT_METHOD && paymentMethodToDelete) {
      return (
        <Button
          emphasis="filled"
          onClick={async () => {
            setIsLoading(true);
            paymentMethodToDelete &&
              deletePaymentMethod({ postData: { encodedPaymentMethodId: paymentMethodToDelete.id } });
            setIsLoading(false);
          }}
          loading={isLoading}
        >
          Yes, delete account
        </Button>
      );
    } else if (
      step === STEPS.DELETE_SCHEDULED_PAYMENT_METHOD_WHEN_MULTIPLE_ACTIVE_PAYMENT_METHODS &&
      paymentMethodToDelete
    ) {
      return (
        <Button
          emphasis="filled"
          disabled={Boolean(
            (moveInCostsDetails?.paymentMethodId && !selectedNewPaymentMethods?.moveInCostsId) ||
              (paymentSubscriptionDetails?.paymentMethodId && !selectedNewPaymentMethods?.monthlyRentId),
          )}
          onClick={async () => {
            setIsLoading(true);
            await onContinueFromDeleteScheduledPaymentMethod({
              moveInCostsPaymentMethodId: selectedNewPaymentMethods?.moveInCostsId,
              monthlyRentPaymentMethodId: selectedNewPaymentMethods?.monthlyRentId,
            });
            setIsLoading(false);
          }}
          loading={isLoading}
        >
          Continue
        </Button>
      );
    } else if (
      step === STEPS.DELETE_SCHEDULED_PAYMENT_METHOD_WHEN_ONE_OTHER_ACTIVE_PAYMENT_METHOD &&
      paymentMethodToDelete
    ) {
      return (
        <DebouncedButton
          emphasis="filled"
          onClick={async () => {
            setIsLoading(true);
            await onSubmitDeletePaymentMethod({
              moveInCostsPaymentMethodId: selectedMoveInCostsPaymentMethodDetails?.id,
              monthlyRentPaymentMethodId: selectedMonthlyRentPaymentMethodDetails?.id,
              paymentMethodIdToDelete: paymentMethodToDelete?.id,
              deletePaymentMethodFn: deletePaymentMethod,
            });
            setIsLoading(false);
          }}
          loading={isLoading}
        >
          Submit
        </DebouncedButton>
      );
    } else if (step === STEPS.VERIFICATION_IN_PROGRESS) {
      return (
        <Button emphasis="filled" onClick={onCancel}>
          Return to dashboard
        </Button>
      );
    }
  };
  return (
    <Modal
      open={true}
      trigger={null}
      size="md"
      header={<Heading level={2}>Manage payment methods</Heading>}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onCancel();
        }
      }}
      body={
        <>
          <Box css={{ marginBottom: 'default' }}>
            <NotificationContainer modal persona={userPersonas.RENTER} />
          </Box>
          {step === STEPS.DISPLAY_PAYMENT_METHODS && (
            <DisplayPaymentMethods
              formattedAddress={formattedAddress}
              onPaymentMethodClick={(paymentMethod) => {
                // Only bank accounts can have Plaid verification statuses
                const isBankAccount = paymentMethod.accountType === 'bank_account';

                if (isBankAccount && paymentMethod.paymentMethodStatus === 'pendingAutomaticVerification') {
                  setStep(STEPS.VERIFICATION_IN_PROGRESS);
                } else if (isBankAccount && paymentMethod.paymentMethodStatus === 'pendingManualVerification') {
                  setStep(STEPS.VERIFY_BANK_ACCOUNT);
                } else if (isBankAccount && paymentMethod.paymentMethodStatus === VERIFICATION_STATUS.PLAID_REVOKED) {
                  setStep(STEPS.RECONNECT_BANK_ACCOUNT);
                } else {
                  setStep(STEPS.DISPLAY_PAYMENT_METHOD_DETAILS);
                }
                setPaymentMethodToDelete(paymentMethod);
              }}
            />
          )}
          {step === STEPS.DISPLAY_PAYMENT_METHOD_DETAILS && paymentMethodToDelete && (
            <PaymentMethodDetail
              onDeletePaymentMethodClick={() => {
                const verifiedPaymentMethods = getVerifiedPaymentMethods(paymentMethods);
                if (verifiedPaymentMethods.length === 1) {
                  setStep(STEPS.DISPLAY_MUST_HAVE_AT_LEAST_ONE_PAYMENT_METHOD);
                } else if (
                  paymentMethodToDelete.id !== paymentSubscriptionDetails.paymentMethodId &&
                  paymentMethodToDelete.id !== moveInCostsDetails?.paymentMethodId
                ) {
                  setStep(STEPS.DELETE_NOT_SCHEDULED_PAYMENT_METHOD);
                } else if (verifiedPaymentMethods.length === 2) {
                  setSelectedMonthlyRentPaymentMethodDetails(
                    verifiedPaymentMethods.find((paymentMethod) => paymentMethod.id !== paymentMethodToDelete.id) ||
                      null,
                  );
                  setSelectedMoveInCostsPaymentMethodDetails(
                    verifiedPaymentMethods.find((paymentMethod) => paymentMethod.id !== paymentMethodToDelete.id) ||
                      null,
                  );
                  setStep(STEPS.DELETE_SCHEDULED_PAYMENT_METHOD_WHEN_ONE_OTHER_ACTIVE_PAYMENT_METHOD);
                } else {
                  setStep(STEPS.DELETE_SCHEDULED_PAYMENT_METHOD_WHEN_MULTIPLE_ACTIVE_PAYMENT_METHODS);
                }
              }}
              paymentMethod={paymentMethodToDelete}
              paymentMethodTypes={paymentMethodTypes}
            />
          )}
          {step === STEPS.DISPLAY_MUST_HAVE_AT_LEAST_ONE_PAYMENT_METHOD && paymentMethodToDelete && (
            <MustHaveAtLeastOnePaymentMethod paymentMethodToDelete={paymentMethodToDelete} />
          )}
          {step === STEPS.VERIFY_BANK_ACCOUNT && (
            <VerifyBankAccount
              onDeletePaymentMethodClick={() =>
                paymentMethodToDelete &&
                deletePaymentMethod({ postData: { encodedPaymentMethodId: paymentMethodToDelete.id } })
              }
            />
          )}
          {step === STEPS.VERIFICATION_IN_PROGRESS && (
            <VerificationInProgress
              onDeletePaymentMethodClick={() =>
                paymentMethodToDelete &&
                deletePaymentMethod({ postData: { encodedPaymentMethodId: paymentMethodToDelete.id } })
              }
            />
          )}
          {step === STEPS.DELETE_NOT_SCHEDULED_PAYMENT_METHOD && paymentMethodToDelete && (
            <DeletePaymentMethodConfirmation paymentMethodToDelete={paymentMethodToDelete} />
          )}
          {step === STEPS.DELETE_SCHEDULED_PAYMENT_METHOD_WHEN_MULTIPLE_ACTIVE_PAYMENT_METHODS &&
            paymentMethodToDelete && (
              <DeleteScheduledPaymentMethod
                paymentMethodToDelete={paymentMethodToDelete}
                paymentMethods={paymentMethods}
                paymentSubscriptionDetails={paymentSubscriptionDetails}
                moveInCostsDetails={moveInCostsDetails}
                setSelectedNewPaymentMethods={(newPaymentMethods) => setSelectedNewPaymentMethods(newPaymentMethods)}
                selectedNewPaymentMethods={selectedNewPaymentMethods}
              />
            )}
          {step === STEPS.DELETE_SCHEDULED_PAYMENT_METHOD_WHEN_ONE_OTHER_ACTIVE_PAYMENT_METHOD &&
            paymentMethodToDelete && (
              <DeleteOneOtherActivePaymentMethod
                paymentMethodToDelete={paymentMethodToDelete}
                selectedMoveInCostsPaymentMethodDetails={selectedMoveInCostsPaymentMethodDetails}
                selectedMonthlyRentPaymentMethodDetails={selectedMonthlyRentPaymentMethodDetails}
                paymentSubscriptionDetails={paymentSubscriptionDetails}
                moveInCostsDetails={moveInCostsDetails}
                formattedAddress={formattedAddress}
              />
            )}
          {step === STEPS.RECONNECT_BANK_ACCOUNT && paymentMethodToDelete && (
            <ReconnectBankAccount
              accountName={paymentMethodToDelete.nickname}
              accountLast4={paymentMethodToDelete.accountLast4}
              onDeletePaymentMethodClick={() => {
                paymentMethodToDelete &&
                  deletePaymentMethod({ postData: { encodedPaymentMethodId: paymentMethodToDelete.id } });
              }}
            />
          )}
        </>
      }
      footer={
        <ButtonGroup aria-label="action-buttons">
          <TextButton
            onClick={
              step === STEPS.DISPLAY_PAYMENT_METHODS ||
              (step === STEPS.DELETE_SCHEDULED_PAYMENT_METHOD_WHEN_ONE_OTHER_ACTIVE_PAYMENT_METHOD &&
                paymentMethodToDelete)
                ? onCancel
                : onBackClick
            }
          >
            {step === STEPS.DISPLAY_PAYMENT_METHODS ||
            (step === STEPS.DELETE_SCHEDULED_PAYMENT_METHOD_WHEN_ONE_OTHER_ACTIVE_PAYMENT_METHOD &&
              paymentMethodToDelete)
              ? 'Cancel'
              : 'Back'}
          </TextButton>
          {renderPrimaryButton()}
        </ButtonGroup>
      }
    />
  );
};

export default ManagePaymentMethodsPage;
